// FormTab.tsx
import { useCallback, useEffect, useState } from 'react';
import { Modal, Button, Center, Loader, Stack, Text, Group } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useCourseCustomFieldService } from '@actschurch/ccms-sdk';

import { CourseFormPage, CourseFormViewer } from '../CustomForms/CourseCustomField';

export interface FormTabProps {
  courseId: number;
  active: boolean;
  onCourseChange?: () => void;
}

export default function FormTab({ courseId, active, onCourseChange }: FormTabProps) {
  const courseCustomField = useCourseCustomFieldService();

  const [loading, setLoading] = useState(true);
  const [hasForm, setHasForm] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

 const checkForm = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fields = await courseCustomField.byCourse(courseId);
      setHasForm(Array.isArray(fields) && fields.length > 0);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Failed to load form');
      setHasForm(false);
    } finally {
      setLoading(false);
    }
  }, [courseCustomField, courseId]);

  if (error) {
    return (
      <Center py="xl">
        <Text c="red" size="sm">{error}</Text>
      </Center>
    );
  }

  useEffect(() => {
    if (active) checkForm();
  }, [active, checkForm]);

  const handleCloseBuilder = () => {
    setBuilderOpen(false);
    // Re-check + bubble up so the parent course record can refresh if needed
    checkForm();
    onCourseChange?.();
  };

  if (!active) return null;

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <>
      {!hasForm ? (
        <Stack align="center" py="xl" gap="sm">
          <Text c="dimmed" size="sm">
            No custom form has been built for this course yet.
          </Text>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setBuilderOpen(true)}
          >
            Add Custom Form
          </Button>
        </Stack>
      ) : (
        <Stack gap="md">
          <Group justify="flex-end">
            <Button
              variant="light"
              leftSection={<IconPlus size={16} />}
              onClick={() => setBuilderOpen(true)}
            >
              Edit Form
            </Button>
          </Group>
          <CourseFormViewer courseID={courseId} />
        </Stack>
      )}

      <Modal
        opened={builderOpen}
        onClose={handleCloseBuilder}
        title="Course Custom Form Builder"
        size="xl"
        fullScreen
      >
        <CourseFormPage courseID={courseId} />
      </Modal>
    </>
  );
}